/**
 * Product Templates Utility
 * Manages saving, loading, and deleting product templates
 */

const TEMPLATES_KEY = 'product-templates';

/**
 * Get all product templates
 * @returns {Array} Array of template objects
 */
export function getTemplates() {
  try {
    const stored = localStorage.getItem(TEMPLATES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (err) {
    console.error('Failed to load templates:', err);
    return [];
  }
}

/**
 * Save a new template
 * @param {string} name - Template name
 * @param {object} productData - Product data to save as template
 * @returns {object} The saved template
 */
export function saveTemplate(name, productData) {
  try {
    const templates = getTemplates();

    // Create template object (exclude id, timestamps, and some fields)
    const template = {
      id: Date.now(),
      name: name.trim(),
      createdAt: new Date().toISOString(),
      data: {
        // Basic info
        name: productData.name || '',
        sku: '', // Don't copy SKU (must be unique)
        description: productData.description || '',
        shortDescription: productData.shortDescription || '',

        // Pricing
        price: productData.price || 0,
        compareAtPrice: productData.compareAtPrice || null,
        costPerItem: productData.costPerItem || null,

        // Organization
        category: productData.category || '',
        tags: productData.tags || [],

        // Inventory (reset stock to 0)
        stock: 0,
        lowStockThreshold: productData.lowStockThreshold || 10,
        trackInventory: productData.trackInventory ?? true,

        // Status (always start as draft)
        status: 'DRAFT',
        isFeatured: false,

        // Product details
        dimensions: productData.dimensions || { length: '', width: '', height: '', unit: 'cm' },
        weight: productData.weight || { value: '', unit: 'kg' },
        materials: productData.materials || '',
        artist: productData.artist || '',

        // Features (copy structure)
        features: productData.features || [],

        // Images (don't copy actual images)
        images: [],

        // SEO
        seoTitle: '',
        seoDescription: '',
        seoKeywords: productData.seoKeywords || [],
      },
    };

    templates.push(template);
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));

    return template;
  } catch (err) {
    console.error('Failed to save template:', err);
    throw err;
  }
}

/**
 * Delete a template
 * @param {number} templateId - Template ID to delete
 */
export function deleteTemplate(templateId) {
  try {
    const templates = getTemplates();
    const filtered = templates.filter((t) => t.id !== templateId);
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(filtered));
  } catch (err) {
    console.error('Failed to delete template:', err);
    throw err;
  }
}

/**
 * Get a template by ID
 * @param {number} templateId - Template ID
 * @returns {object|null} Template object or null if not found
 */
export function getTemplate(templateId) {
  const templates = getTemplates();
  return templates.find((t) => t.id === templateId) || null;
}

/**
 * Update a template
 * @param {number} templateId - Template ID
 * @param {object} updates - Updates to apply
 */
export function updateTemplate(templateId, updates) {
  try {
    const templates = getTemplates();
    const index = templates.findIndex((t) => t.id === templateId);

    if (index === -1) {
      throw new Error('Template not found');
    }

    templates[index] = {
      ...templates[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
  } catch (err) {
    console.error('Failed to update template:', err);
    throw err;
  }
}

/**
 * Apply a template to create new product data
 * @param {number} templateId - Template ID to apply
 * @returns {object|null} Product data from template or null if not found
 */
export function applyTemplate(templateId) {
  const template = getTemplate(templateId);
  if (!template) return null;

  // Return a fresh copy of the template data
  return JSON.parse(JSON.stringify(template.data));
}
